import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ProcedureChipPopoverContents from 'procedures/chip-utils/ProcedureChipPopoverContents';
import {getProcedureIcon} from 'procedures/design/procedureIconUtils';
import {procedurePageUrl} from 'procedures/network/procedureUrls';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';

import SmartChip from './SmartChip';
import useSmartChipQuery, {SmartChipType} from './useSmartChipQuery';

// Pass this on `flowchartVersionId` prop to render an error chip (e.g., a withdrawn procedure where one is required)
const NO_PUBLISHED_VERSION = 'NO_PUBLISHED_VERSION';

export interface ProcedureChipProps extends SharedSmartChipProps<SmartChipType.PROCEDURE> {
  flowchartVersionId?: string | typeof NO_PUBLISHED_VERSION;
}

export default function ProcedureChip(props: ProcedureChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;
  const isUnpublished = props.flowchartVersionId === NO_PUBLISHED_VERSION;
  const flowchartVersionForUrlConstruction = isUnpublished ? undefined : props.flowchartVersionId;

  const {
    item: procedure,
    handleClickLink,
    isInaccessible,
    itemUrl: procedureUrl,
  } = useSmartChipQuery<SmartChipType.PROCEDURE>({
    id,
    onClick,
    getUrlFn: (procedure) =>
      procedurePageUrl(procedure.id, {procedureFlowchartVersionId: flowchartVersionForUrlConstruction}),
  });
  const icon = procedure ? getProcedureIcon(procedure.procedureType) : null;

  return (
    <SmartChip
      {...sharedSmartChipProps}
      className={
        isUnpublished && BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING ? 'chip--warningLighter' : null
      }
      icon={icon}
      isInaccessible={isInaccessible}
      item={procedure}
      onClick={handleClickLink}
      url={procedureUrl}
      popoverContentOverride={
        id != null && !isInaccessible ? (
          <ProcedureChipPopoverContents id={id} flowchartVersionId={props.flowchartVersionId} />
        ) : null
      }
    />
  );
}

ProcedureChip.NO_PUBLISHED_VERSION = NO_PUBLISHED_VERSION;
